/* ============================================================================
   Tour do administrador escolar — DEFINIÇÃO DOS PASSOS
   ============================================================================
   Dados puros: nenhum import, nenhum React, nenhum DOM. O formato de um passo e
   as constantes compartilhadas estão em ./index.js.

   O ESCOPO DESTE PAPEL É A ESCOLA (role_id 4). É o que separa o tom deste roteiro
   do do professor: o professor cuida das turmas dele, o adm escolar cuida da
   unidade — quem está cadastrado, em que turma, com que série e disciplina. Daí a
   jornada começar por Cadastro e só depois olhar atividades e avaliações.

   "TUDO RELACIONADO A CADASTRO" — COMO ISSO FOI RESOLVIDO
   O menu vem do backend e não há como saber daqui o que a seção Cadastro traz em
   cada cliente. Então o roteiro ataca por dois lados:

   1. um passo na PRÓPRIA SEÇÃO (`menu-cadastro`), achado pelo nome. Ele enquadra a
      área inteira e aparece mesmo que nenhum dos filhos abaixo exista;
   2. um passo por destino de cadastro que existe de fato no código — usuários,
      séries, disciplinas e turmas. Cada um cai sozinho se aquele cliente não tiver
      o item, sem levar os outros.

   Ficaram DE FORA de propósito: `/school/list` (o adm escolar administra uma
   escola, não a lista delas) e as outras telas de `pages/Classification`
   (habilidade, unidade temática, segmento…), que são taxonomia pedagógica e
   costumam viver numa seção de Classificação, não em Cadastro. As duas coisas são
   um passo a mais aqui se o menu do cliente as tiver.

   O BLOCO DE TURMAS É O ÚNICO QUE NAVEGA
   As cinco ações pedidas em 03/08/2026 — criar, editar, importar estudantes,
   membros e estatísticas — só existem DENTRO de /classroom/list, e quatro delas são
   botões de LINHA da tabela compartilhada. Por isso `cadastro-turmas` é guiado: o
   tour precisa estar na tela para poder apontar.

   Este bloco já saiu e voltou. Saiu em 03/08/2026 porque o balão do passo de Turmas
   aparecia cortado — os filhos da seção Cadastro caem abaixo da área visível da
   sidebar — e voltou quando a causa foi corrigida na raiz (o tour passou a rolar a
   sidebar até o alvo, ver ../posicionamento.js).

   ÂNCORAS QUE NÃO SÃO ÓBVIAS

   - **Cadastro** é seção com filhos, e cabeçalho de seção não tem href: o Sidebar
     passa `to` ao CSidebarNavDropdown, que vira atributo do <li>. Vai por nome. E
     como todo dropdown do AVA nasce fechado, é o `abrirDropdowns` que revela os
     filhos nos passos seguintes.
   - **Inteligência Educacional** redireciona /inteligencia-educacional para
     /inteligencia-educacional/admin (o papel 4 é "admin" na allowlist de
     pages/InteligenciaEducacional/config/perfilPorRole.js). Quem cobre isso é o
     casamento por prefixo do `rotaCombina`.
   ========================================================================== */

const PASSOS = [
  {
    id: "boas-vindas",
    alvo: "body",
    posicao: "center",
    verificarNoInicio: false,
    titulo: "Vamos conhecer a plataforma",
    texto:
      "Em poucos passos, você conhecerá os principais recursos da plataforma. " +
      "Vamos mostrar onde cadastrar pessoas e turmas, importar alunos em lote e " +
      "acompanhar atividades e avaliações. Você pode encerrar este tour quando " +
      "quiser e retomá-lo pelo ícone de ajuda no topo da tela."
  },

  /* -------------------------------------------------------------- cadastro -- */
  {
    id: "menu-cadastro",
    // Sem seletor de href: cabeçalho de seção com filhos não tem link.
    alvoPorTexto: ["cadastro"],
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Área de Cadastro",
    texto:
      "Nesta área, você encontra os cadastros que organizam a estrutura da sua " +
      "escola na plataforma, como usuários, turmas, séries e disciplinas. Vamos " +
      "conhecer os principais itens."
  },
  {
    id: "cadastro-usuarios",
    alvo:
      '.c-sidebar-nav-link[href="/userlist"], ' +
      '.c-sidebar-nav-link[href="/Userlist"]',
    alvoPorTexto: ["usuario"],
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Usuários",
    texto:
      "Aqui estão cadastradas todas as pessoas da escola, como alunos, " +
      "professores e equipe administrativa. Você pode consultar os usuários, " +
      "editar informações e alterar seus perfis de acesso."
  },
  {
    id: "cadastro-series",
    alvo: '.c-sidebar-nav-link[href="/serie/list"]',
    alvoPorTexto: ["serie"],
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Séries",
    texto:
      "Nesta seção, você gerencia as séries atendidas pela escola. Elas servem " +
      "como base para a criação das turmas e ajudam a garantir que cada aluno " +
      "receba o conteúdo correspondente ao seu ano escolar."
  },
  {
    id: "cadastro-disciplinas",
    alvo: '.c-sidebar-nav-link[href="/discipline/list"]',
    alvoPorTexto: ["disciplina"],
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Disciplinas",
    texto:
      "Aqui ficam cadastradas as disciplinas oferecidas pela escola. Elas são " +
      "utilizadas na organização das turmas e na criação de atividades e " +
      "avaliações pelos professores."
  },

  /* --------------------------------------------- turmas, na própria tela ---- */
  {
    id: "cadastro-turmas",
    alvo: '.c-sidebar-nav-link[href="/classroom/list"]',
    alvoPorTexto: ["turma"],
    posicao: "right",
    verificarNoInicio: true,
    rotaEsperada: "/classroom/list",
    permiteCliqueNoAlvo: true,
    titulo: "Turmas",
    texto:
      "Nesta seção, você gerencia as turmas da escola, definindo série, " +
      "disciplinas, professores e alunos de cada uma. Abra a listagem para " +
      "conhecer as ações disponíveis."
  },
  {
    id: "turmas-criar",
    alvo: '[data-tour="tabela-novo"]',
    posicao: "bottom",
    verificarNoInicio: false,
    dependeDe: "cadastro-turmas",
    titulo: "Criar uma turma",
    texto:
      "Este botão abre o cadastro de uma nova turma, no qual você define o nome, " +
      "o ano letivo, a série e as disciplinas. Após a conclusão do cadastro, a " +
      "turma já pode receber professores e alunos."
  },
  {
    id: "turmas-editar",
    alvo: "#editBtn-0",
    posicao: "left",
    verificarNoInicio: false,
    dependeDe: "cadastro-turmas",
    titulo: "Editar a turma",
    texto:
      "Permite ajustar os dados de uma turma já existente, como o nome, a série " +
      "ou as disciplinas vinculadas. É a mesma tela do cadastro, apresentada com " +
      "as informações atuais."
  },
  {
    id: "turmas-importar",
    alvo: "#importStudentsBtn-0",
    posicao: "left",
    verificarNoInicio: false,
    // A partir daqui a dependência é ENCADEADA, e não toda em `cadastro-turmas`.
    // O motivo é o runtime: os quatro botões são de LINHA da tabela e só existem
    // se a escola já tiver ao menos uma turma. Encadeando, uma escola sem turma
    // nenhuma perde os quatro de uma vez, na primeira espera que falha — em vez
    // de esperar o timeout de cada um, um a um (ver `proximoApos` em ../filtro.js).
    dependeDe: "turmas-editar",
    titulo: "Importar estudantes",
    texto:
      "Permite incluir vários alunos de uma só vez na turma, a partir de uma " +
      "planilha, em vez de cadastrá-los individualmente. Cada turma possui o seu " +
      "próprio botão, na linha correspondente."
  },
  {
    id: "turmas-membros",
    alvo: "#membersBtn-0",
    posicao: "left",
    verificarNoInicio: false,
    dependeDe: "turmas-importar",
    titulo: "Membros da turma",
    texto:
      "Apresenta os alunos e professores vinculados à turma, permitindo incluir " +
      "ou remover participantes individualmente. É também onde conferir se " +
      "alguém não foi incluído durante uma importação."
  },
  {
    id: "turmas-estatisticas",
    // O `btnStatistics` da tabela compartilhada reaproveita o id `viewBtn-{linha}`
    // do `btnView`. Nesta tela só um dos dois está ligado, mas a classe desfaz a
    // ambiguidade de qualquer forma.
    alvo: "#viewBtn-0.btn-statistics",
    posicao: "left",
    verificarNoInicio: false,
    dependeDe: "turmas-membros",
    titulo: "Estatísticas da turma",
    texto:
      "Reúne os indicadores da turma, como a participação nas atividades e a " +
      "evolução ao longo do período. É a visão mais rápida para identificar onde " +
      "a escola precisa dedicar mais atenção."
  },

  /* ------------------------------------------------------------ atividades -- */
  {
    id: "menu-atividades",
    alvo: '.c-sidebar-nav-link[href="/activity/list"]',
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Minhas Atividades",
    texto:
      "Nesta área, você acompanha as atividades criadas pelos professores e " +
      "enviadas às turmas. É uma forma prática de visualizar o que está sendo " +
      "aplicado em toda a escola."
  },

  /* ------------------------------------------------------------ avaliações -- */
  {
    id: "menu-avaliacoes",
    alvo: '.c-sidebar-nav-link[href="/evaluation/list"]',
    alvoPorTexto: ["avaliacoes", "avaliacao"],
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Minhas Avaliações",
    texto:
      "Aqui você encontra as avaliações aplicadas na escola. Além de criar e " +
      "enviar avaliações para as turmas, é possível acompanhar as correções e o " +
      "desempenho de cada aluno."
  },

  /* ------------------------------------------- inteligência educacional ----- */
  {
    id: "menu-inteligencia-educacional",
    alvo: '.c-sidebar-nav-link[href="/inteligencia-educacional"]',
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Inteligência Educacional",
    texto:
      "Este painel reúne informações importantes sobre turmas, disciplinas e " +
      "alunos, facilitando o acompanhamento do desempenho e apoiando a tomada de " +
      "decisões pedagógicas."
  },

  {
    id: "fim",
    alvo: "body",
    posicao: "center",
    verificarNoInicio: false,
    titulo: "Tour concluído!",
    texto:
      "Agora você já conhece os principais recursos da plataforma. Sempre que " +
      "quiser revisar este tour, basta clicar no ícone de ajuda localizado no " +
      "topo da tela."
  }
];

export default PASSOS;
